<template>
  <div class="elite-tabs-wrapper-content">
    <div class="row">
      <!-- Informations principales -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h4 class="mb-0">{{ $t("ELIXIOPAY.TRANSACTION_INFO") }}</h4>
          </div>
          <div class="card-body">
            <dl class="row">
              <dt class="col-sm-5">{{ $t("ELIXIOPAY.PROVIDER") }}</dt>
              <dd class="col-sm-7">
                <el-tag
                  :type="getProviderTagType(transaction.provider)"
                  size="small"
                >
                  {{ getProviderLabel(transaction.provider) }}
                </el-tag>
              </dd>
            </dl>

            <dl class="row">
              <dt class="col-sm-5">
                {{ $t("ELIXIOPAY.PROVIDER_TRANSACTION_ID") }}
              </dt>
              <dd class="col-sm-7 text-monospace">
                {{ transaction.provider_transaction_id }}
              </dd>
            </dl>

            <dl class="row">
              <dt class="col-sm-5">{{ $t("COMMON.STATUS") }}</dt>
              <dd class="col-sm-7">
                <el-tag
                  :type="getStatusTagType(transaction.status)"
                  size="small"
                >
                  {{ getStatusLabel(transaction.status) }}
                </el-tag>
              </dd>
            </dl>

            <dl class="row">
              <dt class="col-sm-5">{{ $t("COMMON.AMOUNT") }}</dt>
              <dd class="col-sm-7">
                <strong class="text-success">{{
                  formatAmount(
                    transaction.amount ?? transaction.invoice?.totals?.total,
                    transaction.invoice?.currency ?? null
                  )
                }}</strong>
              </dd>
            </dl>

            <dl class="row" v-if="transaction.payment_method">
              <dt class="col-sm-5">{{ $t("ELIXIOPAY.PAYMENT_METHOD") }}</dt>
              <dd class="col-sm-7">
                {{ getPaymentMethodLabel(transaction.payment_method) }}
              </dd>
            </dl>

            <dl class="row" v-if="transaction.error_message">
              <dt class="col-sm-5">{{ $t("COMMON.ERROR") }}</dt>
              <dd class="col-sm-7 text-danger">
                {{ transaction.error_message }}
              </dd>
            </dl>
          </div>
        </div>
      </div>

      <!-- Informations client/organisation -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h4 class="mb-0">{{ $t("ELIXIOPAY.RELATED_INFO") }}</h4>
          </div>
          <div class="card-body">
            <dl class="row" v-if="transaction.organization">
              <dt class="col-sm-5">{{ $t("COMMON.ORGANIZATION") }}</dt>
              <dd class="col-sm-7">
                {{ transaction.organization.name }}
              </dd>
            </dl>

            <dl
              class="row"
              v-if="transaction.payable_type && transaction.payable"
            >
              <dt class="col-sm-5">{{ $t("ELIXIOPAY.PAYABLE") }}</dt>
              <dd class="col-sm-7">
                <span class="badge badge-info">{{
                  getPayableTypeLabel(transaction.payable_type)
                }}</span>
                <br />
                <small class="text-muted"
                  >ID: {{ transaction.payable_id }}</small
                >
              </dd>
            </dl>

            <dl class="row" v-if="transaction.thirdPartyService">
              <dt class="col-sm-5">{{ $t("ELIXIOPAY.SERVICE") }}</dt>
              <dd class="col-sm-7">
                {{
                  transaction.thirdPartyService.service_name ||
                  transaction.thirdPartyService.service_type
                }}
              </dd>
            </dl>

            <dl class="row">
              <dt class="col-sm-5">{{ $t("COMMON.CREATED_AT") }}</dt>
              <dd class="col-sm-7">
                {{ transaction.created_at | moment("LLLL") }}
              </dd>
            </dl>

            <dl class="row">
              <dt class="col-sm-5">{{ $t("COMMON.UPDATED_AT") }}</dt>
              <dd class="col-sm-7">
                {{ transaction.updated_at | moment("LLLL") }}
              </dd>
            </dl>
          </div>
        </div>
      </div>

      <!-- Métadonnées -->
      <div
        class="col-12 mt-3"
        v-if="
          transaction.metadata && Object.keys(transaction.metadata).length > 0
        "
      >
        <div class="card">
          <div class="card-header">
            <h4 class="mb-0">{{ $t("ELIXIOPAY.METADATA") }}</h4>
          </div>
          <div class="card-body">
            <pre class="bg-light p-3 rounded">{{
              JSON.stringify(transaction.metadata, null, 2)
            }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tag } from "element-ui";

export default {
  name: "elixiopay-transaction-view-global",

  components: {
    [Tag.name]: Tag,
  },

  props: {
    transaction: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {};
  },

  methods: {
    getProviderLabel(provider) {
      const labels = {
        pandapay: "PandaPay",
        stripe: "Stripe",
        nuvei: "Nuvei",
        paypal: "PayPal",
      };
      return labels[provider] || provider;
    },

    getProviderTagType(provider) {
      const types = {
        pandapay: "primary",
        stripe: "success",
        nuvei: "warning",
        paypal: "info",
      };
      return types[provider] || "";
    },

    getStatusLabel(status) {
      const labels = {
        pending: this.$t("ELIXIOPAY.STATUS_PENDING"),
        authorized: this.$t("ELIXIOPAY.STATUS_AUTHORIZED"),
        captured: this.$t("ELIXIOPAY.STATUS_CAPTURED"),
        paid: this.$t("ELIXIOPAY.STATUS_PAID"),
        failed: this.$t("ELIXIOPAY.STATUS_FAILED"),
        refunded: this.$t("ELIXIOPAY.STATUS_REFUNDED"),
        cancelled: this.$t("ELIXIOPAY.STATUS_CANCELLED"),
      };
      return labels[status] || status;
    },

    getStatusTagType(status) {
      const types = {
        pending: "warning",
        authorized: "info",
        captured: "success",
        paid: "success",
        failed: "danger",
        refunded: "warning",
        cancelled: "info",
      };
      return types[status] || "";
    },

    getPaymentMethodLabel(method) {
      const labels = {
        CB: this.$t("ELIXIOPAY.METHOD_CB"),
        TFE: this.$t("ELIXIOPAY.METHOD_TFE"),
        card: this.$t("ELIXIOPAY.METHOD_CARD"),
      };
      return labels[method] || method;
    },

    getPayableTypeLabel(type) {
      if (type.includes("Customer")) return this.$t("COMMON.CUSTOMER");
      if (type.includes("Invoice")) return this.$t("COMMON.INVOICE");
      if (type.includes("Order")) return this.$t("COMMON.ORDER");
      return type.split("\\").pop();
    },

    formatAmount(amount, currency) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: currency || "CAD",
      }).format(amount);
    },

    transactionUpdated() {
      this.$emit("transactionUpdated", true);
    },
  },

  mounted() {},

  watch: {
    transaction(newTransaction) {
      // Réagir aux changements de transaction si nécessaire
    },
  },
};
</script>

<style scoped>
.card {
  box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
  border: none;
}

.card-header {
  background-color: #f8f9fc;
  border-bottom: 1px solid #e3e6f0;
}

dl.row {
  margin-bottom: 0.5rem;
}

dt {
  font-weight: 600;
  color: #5a5c69;
}

dd {
  color: #3a3b45;
}

pre {
  font-size: 0.85rem;
  max-height: 400px;
  overflow-y: auto;
}
</style>
